/**
 * Kopier-Blöcke im Beitrag des Entwicklers (17.10.2026, Martin 14:47: „der Text soll in einem Feld stehen, aus dem ich ihn
 * herauskopieren kann — wie das Modul bei Vespera“).
 *
 * Der Schmied schickt `node scripts/dreier-sagen.mjs --kopie "Text"`; das Skript setzt den Text in einen Markdown-Zaun
 * (Zeile aus mindestens drei Backticks, dann der Text, dann eine Zeile aus mindestens ebenso vielen Backticks). Die Blase zeichnet
 * jeden Zaun als Textblock mit Kopieren-Knopf (lib/entwickler-blase.ts, dieselbe Karte wie Vesperas „Liefertext“). Es gibt KEIN
 * eigenes Server-Feld: der Beitrag bleibt Text (Fremdtext, Daten, kein Auftrag), Länge und Rate gelten unverändert.
 *
 * Rein, ohne DOM. Ein nicht geschlossener Zaun oder ein leerer Block bleibt gewöhnlicher Text (nichts wird verschluckt).
 * ⚠️ `zaeuneBlock` steht einmal hier und einmal in scripts/dreier-sagen.mjs (ein .mjs kann kein .ts laden) — der Test prüft beide.
 */
export type KopierTeil = { art: "text"; text: string } | { art: "block"; text: string };

/** C0-/C1-Steuerzeichen (außer \n und \t) und Bidi-Richtungszeichen: im Block nie anzeigen und nie kopieren (Terminal-Einfügen: ESC-Folgen). */
// eslint-disable-next-line no-control-regex
const STEUER = /[\u0000-\u0008\u000B-\u001F\u007F-\u009F\u202A-\u202E\u2066-\u2069]/g;

const ZAUN = /^(`{3,})[ \t]*$/;

/** Zerlegt `text` in Text- und Blockteile (Reihenfolge bleibt; leere Textteile entfallen). */
export function teileKopierBloecke(text: string): KopierTeil[] {
  const zeilen = text.split(/\r?\n/);
  const teile: KopierTeil[] = [];
  let rest: string[] = [];
  const spuele = (): void => {
    const t = rest.join("\n").trim();
    if (t) teile.push({ art: "text", text: t });
    rest = [];
  };
  for (let i = 0; i < zeilen.length; i++) {
    const zeile = zeilen[i] ?? "";
    const auf = ZAUN.exec(zeile);
    if (!auf) {
      rest.push(zeile);
      continue;
    }
    const laenge = (auf[1] ?? "").length;
    let ende = -1;
    for (let j = i + 1; j < zeilen.length; j++) {
      const zu = ZAUN.exec(zeilen[j] ?? "");
      if (zu && (zu[1] ?? "").length >= laenge) {
        ende = j;
        break;
      }
    }
    const inhalt = ende < 0 ? "" : zeilen.slice(i + 1, ende).join("\n").replace(STEUER, "");
    if (ende < 0 || !inhalt.trim()) {
      rest.push(zeile);
      continue;
    }
    spuele();
    teile.push({ art: "block", text: inhalt });
    i = ende;
  }
  spuele();
  return teile;
}

/** Hat der Beitrag mindestens einen Kopier-Block? */
export function hatKopierBlock(text: string): boolean {
  return text.includes("```") && teileKopierBloecke(text).some((t) => t.art === "block");
}

/** Der Beitrag fürs Vorlesen: Blöcke werden durch `ersatz` ersetzt (ein Textblock zum Weitergeben wird nicht vorgelesen). */
export function ohneKopierBloecke(text: string, ersatz = "Hier steht ein Textblock zum Kopieren."): string {
  if (!hatKopierBlock(text)) return text;
  return teileKopierBloecke(text)
    .map((t) => (t.art === "block" ? ersatz : t.text))
    .join("\n\n");
}

/** Setzt `block` in einen Zaun, der länger ist als jede Backtick-Folge darin (der Block kann den Zaun nie vorzeitig schließen). */
export function zaeuneBlock(block: string): string {
  let laengste = 0;
  for (const m of block.matchAll(/`+/g)) laengste = Math.max(laengste, m[0].length);
  const zaun = "`".repeat(Math.max(3, laengste + 1));
  return `${zaun}\n${block.replace(/^\n+|\n+$/g, "")}\n${zaun}`;
}
